import {
  Activity,
  AlertTriangle,
  BellRing,
  Coins,
  Flame,
  Gauge,
  Loader2,
  Recycle,
  RefreshCw,
  RotateCcw,
  Save,
  Shield,
  ShieldAlert,
  Sliders,
  Sprout,
} from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { adminFactoryReset, adminSeedRoot, fetchAdminConfig, reconcilePositionPool, updateAdminConfig } from '@/api/admin';
import { fmtUsd } from '@/lib/money';

const SystemSubpage = () => {
  const uid = useId();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [form, setForm] = useState({
    min_deposit_usd: '',
    min_withdrawal_usd: '',
    heartbeat_interval_seconds: '60',
    heartbeat_pacing_ratio: '',
    heartbeat_max_cycles_per_tick: '',
    admin_alert_ceiling: '',
    under_attack_mode_enabled: false,
    under_attack_difficulty: 5,
    under_attack_cookie_ttl_seconds: 1800,
    under_attack_message: '',
    min_ltc: '',
    min_doge: '',
    min_trx: '',
    // Patch v16: dynamic auto-repurchase reserve % (stored as string,
    // 0..1 fraction; e.g. "0.20" displays as 20%).
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [lastReconcile, setLastReconcile] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [lastSeed, setLastSeed] = useState(null);
  const [wipePhrase, setWipePhrase] = useState('');
  const [wiping, setWiping] = useState(false);

  const seedRoot = async () => {
    setSeeding(true);
    try {
      const r = await adminSeedRoot();
      setLastSeed(r);
      toast.success('Root planted — matrix engine will pair it on the next tick');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Plant root failed: ${msg}`);
    } finally {
      setSeeding(false);
    }
  };

  const factoryReset = async () => {
    if (wipePhrase !== 'WIPE EVERYTHING') return;
    setWiping(true);
    try {
      const r = await adminFactoryReset(wipePhrase);
      const totals = Object.values(r?.collections_wiped || {}).reduce((a, b) => a + b, 0);
      toast.success(`Factory reset complete — ${totals} docs wiped across ${Object.keys(r?.collections_wiped || {}).length} collections`);
      setWipePhrase('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Reset failed: ${msg}`);
    } finally {
      setWiping(false);
    }
  };

  const reconcile = async () => {
    setReconciling(true);
    try {
      const r = await reconcilePositionPool();
      const report = r?.report || {};
      setLastReconcile(report);
      if ((report.reenqueued ?? 0) > 0) {
        toast.success(`Re-enqueued ${report.reenqueued} position(s)${report.first_was_genesis_seed ? ' — new genesis seed planted' : ''}`);
      } else {
        toast.message('Position pool is already healthy');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Reconcile failed: ${msg}`);
    } finally {
      setReconciling(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const d = await fetchAdminConfig();
      // Guard: backend may return an error-shaped body with a 2xx in some edge
      // cases. Treat anything that doesn't have `defaults` as a load failure.
      if (!d || typeof d !== 'object' || !d.defaults) {
        throw new Error('malformed_response');
      }
      setData(d);
      setForm({
        min_deposit_usd: d.min_deposit_usd,
        min_withdrawal_usd: d.min_withdrawal_usd,
        heartbeat_interval_seconds: String(d.heartbeat_interval_seconds ?? 60),
        heartbeat_pacing_ratio: d.heartbeat_pacing_ratio,
        heartbeat_max_cycles_per_tick: String(d.heartbeat_max_cycles_per_tick),
        admin_alert_ceiling: d.admin_alert_ceiling ?? '',
        under_attack_mode_enabled: Boolean(d.under_attack_mode_enabled),
        under_attack_difficulty: Number(d.under_attack_difficulty ?? 5),
        under_attack_cookie_ttl_seconds: Number(d.under_attack_cookie_ttl_seconds ?? 1800),
        under_attack_message: d.under_attack_message ?? '',
        min_ltc: d.min_ltc ?? '',
        min_doge: d.min_doge ?? '',
        min_trx: d.min_trx ?? '',
      });
    } catch {
      setLoadError(true);
      toast.error('Failed to load system variables');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = () => {
    if (!data) return false;
    return (
      form.min_deposit_usd !== data.min_deposit_usd ||
      form.min_withdrawal_usd !== data.min_withdrawal_usd ||
      String(form.heartbeat_interval_seconds) !== String(data.heartbeat_interval_seconds) ||
      form.heartbeat_pacing_ratio !== data.heartbeat_pacing_ratio ||
      String(form.heartbeat_max_cycles_per_tick) !== String(data.heartbeat_max_cycles_per_tick) ||
      String(form.admin_alert_ceiling) !== String(data.admin_alert_ceiling) ||
      Boolean(form.under_attack_mode_enabled) !== Boolean(data.under_attack_mode_enabled) ||
      Number(form.under_attack_difficulty) !== Number(data.under_attack_difficulty) ||
      Number(form.under_attack_cookie_ttl_seconds) !== Number(data.under_attack_cookie_ttl_seconds) ||
      String(form.under_attack_message) !== String(data.under_attack_message) ||
      String(form.min_ltc) !== String(data.min_ltc) ||
      String(form.min_doge) !== String(data.min_doge) ||
      String(form.min_trx) !== String(data.min_trx)
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        min_deposit_usd: form.min_deposit_usd.trim() || undefined,
        min_withdrawal_usd: form.min_withdrawal_usd.trim() || undefined,
        heartbeat_interval_seconds: form.heartbeat_interval_seconds ? parseInt(form.heartbeat_interval_seconds, 10) : undefined,
        heartbeat_pacing_ratio: form.heartbeat_pacing_ratio.trim() || undefined,
        heartbeat_max_cycles_per_tick: form.heartbeat_max_cycles_per_tick ? parseInt(form.heartbeat_max_cycles_per_tick, 10) : undefined,
        admin_alert_ceiling: form.admin_alert_ceiling?.toString().trim() || undefined,
        under_attack_mode_enabled: Boolean(form.under_attack_mode_enabled),
        under_attack_difficulty: Number(form.under_attack_difficulty),
        under_attack_cookie_ttl_seconds: Number(form.under_attack_cookie_ttl_seconds),
        under_attack_message: (form.under_attack_message ?? '').trim(),
        min_ltc: form.min_ltc?.toString().trim() || undefined,
        min_doge: form.min_doge?.toString().trim() || undefined,
        min_trx: form.min_trx?.toString().trim() || undefined,
      };
      await updateAdminConfig(body);
      toast.success('System variables updated');
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Update failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const restore = () => {
    if (!data?.defaults) return;
    setForm({
      min_deposit_usd: data.defaults.min_deposit_usd,
      min_withdrawal_usd: data.defaults.min_withdrawal_usd,
      heartbeat_pacing_ratio: data.defaults.heartbeat_pacing_ratio,
      heartbeat_max_cycles_per_tick: String(data.defaults.heartbeat_max_cycles_per_tick),
      admin_alert_ceiling: data.defaults.admin_alert_ceiling ?? '2000.00',
      under_attack_mode_enabled: Boolean(data.defaults.under_attack_mode_enabled ?? false),
      under_attack_difficulty: Number(data.defaults.under_attack_difficulty ?? 5),
      under_attack_cookie_ttl_seconds: Number(data.defaults.under_attack_cookie_ttl_seconds ?? 1800),
      under_attack_message: data.defaults.under_attack_message ?? '',
      min_ltc: data.defaults.min_ltc ?? '0.02',
      min_doge: data.defaults.min_doge ?? '10',
      min_trx: data.defaults.min_trx ?? '10',
    });
  };

  return (
    <div className="space-y-6" data-testid="system-subpage">
      <div className="sc-glass rounded-2xl p-5">
        <div className="font-display font-bold text-xl flex items-center gap-2">
          <Sliders className="w-5 h-5 text-[#FFC20E]" />
          System Variable Governor
        </div>
        <p className="text-xs text-[var(--sc-text-dim)] mt-1">
          Scale platform operational configurations without editing raw backend code. Changes persist to the database and are honored by services on the next
          runtime read.
        </p>
      </div>

      {loading ? (
        <div className="sc-glass rounded-2xl p-10 flex items-center justify-center text-[var(--sc-text-dim)] gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : loadError || !data ? (
        <div className="sc-glass rounded-2xl p-8 flex flex-col items-center justify-center gap-3 text-center" data-testid="system-load-error">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{
              background: 'rgba(255, 105, 97, 0.10)',
              border: '1px solid rgba(255, 105, 97, 0.30)',
            }}
          >
            <AlertTriangle className="w-6 h-6 text-[#FF8A82]" />
          </div>
          <div className="font-display font-bold text-lg">Couldn&apos;t load system variables</div>
          <p className="text-[12px] text-[var(--sc-text-dim)] max-w-md leading-relaxed">
            The server is temporarily busy (event-loop spike) or the network blip dropped the response. Your saved settings are safe in the database — this is
            just a load retry.
          </p>
          <button type="button" onClick={load} className="sc-btn-gold mt-2" style={{ padding: '11px 18px', fontSize: 13 }} data-testid="system-load-retry">
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="sc-glass rounded-2xl p-5" data-testid="system-card-min-deposit">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#FFC20E]">
                <Coins className="w-4 h-4" />
                <div className="font-display font-semibold text-sm">Minimum Deposit (USD)</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
                default {data?.defaults?.min_deposit_usd != null ? fmtUsd(data.defaults.min_deposit_usd) : '—'}
              </span>
            </div>
            <input
              className="sc-input mt-3"
              value={form.min_deposit_usd}
              onChange={(e) => setForm((f) => ({ ...f, min_deposit_usd: e.target.value }))}
              data-testid="system-min-deposit"
            />
            <p className="text-[11px] text-[var(--sc-text-dim)] mt-2">Enforced by the deposit pipeline. Members below this threshold are rejected.</p>
          </div>

          <div className="sc-glass rounded-2xl p-5" data-testid="system-card-min-withdrawal">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#FFC20E]">
                <Coins className="w-4 h-4" />
                <div className="font-display font-semibold text-sm">Minimum Withdrawal (USD)</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
                default {data?.defaults?.min_withdrawal_usd != null ? fmtUsd(data.defaults.min_withdrawal_usd) : '—'}
              </span>
            </div>
            <input
              className="sc-input mt-3"
              value={form.min_withdrawal_usd}
              onChange={(e) => setForm((f) => ({ ...f, min_withdrawal_usd: e.target.value }))}
              data-testid="system-min-withdrawal"
            />
            <p className="text-[11px] text-[var(--sc-text-dim)] mt-2">Member &amp; admin withdrawal requests below this value are rejected.</p>
          </div>

          {/* ───────────────────────────────────────────────────────────────
           * Per-asset crypto-unit deposit minimums. These supersede the
           * legacy MIN_DEPOSIT_USD floor (which has been removed from
           * wallet_svc.deposit) because users deposit crypto directly
           * from their own wallet — there's no USD invoice to enforce a
           * dollar minimum against. Each value is in NATIVE crypto units.
           * Values lower than NOWPayments' on-chain dust floor are
           * auto-bumped server-side so users never lose deposits.
           * ───────────────────────────────────────────────────────────── */}
          <div className="sc-glass rounded-2xl p-5" data-testid="system-card-min-ltc">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#FFC20E]">
                <Coins className="w-4 h-4" />
                <div className="font-display font-semibold text-sm">Minimum Deposit (LTC)</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">default {data?.defaults?.min_ltc ?? '0.02'} LTC</span>
            </div>
            <input
              className="sc-input mt-3"
              value={form.min_ltc}
              onChange={(e) => setForm((f) => ({ ...f, min_ltc: e.target.value }))}
              data-testid="system-min-ltc"
              placeholder="0.02"
            />
            <p className="text-[11px] text-[var(--sc-text-dim)] mt-2">Displayed on the deposit page as the &quot;Min&quot; for LTC. Set in native LTC units.</p>
          </div>

          <div className="sc-glass rounded-2xl p-5" data-testid="system-card-min-doge">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#FFC20E]">
                <Coins className="w-4 h-4" />
                <div className="font-display font-semibold text-sm">Minimum Deposit (DOGE)</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">default {data?.defaults?.min_doge ?? '10'} DOGE</span>
            </div>
            <input
              className="sc-input mt-3"
              value={form.min_doge}
              onChange={(e) => setForm((f) => ({ ...f, min_doge: e.target.value }))}
              data-testid="system-min-doge"
              placeholder="10"
            />
            <p className="text-[11px] text-[var(--sc-text-dim)] mt-2">
              Displayed on the deposit page as the &quot;Min&quot; for DOGE. Set in native DOGE units.
            </p>
          </div>

          <div className="sc-glass rounded-2xl p-5" data-testid="system-card-min-trx">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#FFC20E]">
                <Coins className="w-4 h-4" />
                <div className="font-display font-semibold text-sm">Minimum Deposit (TRX)</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">default {data?.defaults?.min_trx ?? '10'} TRX</span>
            </div>
            <input
              className="sc-input mt-3"
              value={form.min_trx}
              onChange={(e) => setForm((f) => ({ ...f, min_trx: e.target.value }))}
              data-testid="system-min-trx"
              placeholder="10"
            />
            <p className="text-[11px] text-[var(--sc-text-dim)] mt-2">Displayed on the deposit page as the &quot;Min&quot; for TRX. Set in native TRX units.</p>
          </div>

          <div className="sc-glass rounded-2xl p-5" data-testid="system-card-interval">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#FFC20E]">
                <Activity className="w-4 h-4" />
                <div className="font-display font-semibold text-sm">Heartbeat Interval (seconds)</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
                default {data?.defaults?.heartbeat_interval_seconds ?? '—'} &middot; 10&ndash;300
              </span>
            </div>
            <input
              type="number"
              min={10}
              max={300}
              step={1}
              className="sc-input mt-3"
              value={form.heartbeat_interval_seconds}
              onChange={(e) => setForm((f) => ({ ...f, heartbeat_interval_seconds: e.target.value }))}
              data-testid="system-heartbeat-interval"
            />
            <p className="text-[11px] text-[var(--sc-text-dim)] mt-2">
              How often the matrix position rotator runs. Lower = faster cycling, higher CPU. Live-applied on save &mdash; no server restart needed.
            </p>
          </div>

          <div className="sc-glass rounded-2xl p-5" data-testid="system-card-pacing">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#FFC20E]">
                <Activity className="w-4 h-4" />
                <div className="font-display font-semibold text-sm">Heartbeat Pacing Ratio</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
                default {data?.defaults?.heartbeat_pacing_ratio ?? '—'}
              </span>
            </div>
            <input
              className="sc-input mt-3"
              value={form.heartbeat_pacing_ratio}
              onChange={(e) => setForm((f) => ({ ...f, heartbeat_pacing_ratio: e.target.value }))}
              data-testid="system-pacing"
            />
            <p className="text-[11px] text-[var(--sc-text-dim)] mt-2">Standard 10% (0.10) pacing rate of the cycle engine. Decimal between 0 and 1.</p>
          </div>

          <div className="sc-glass rounded-2xl p-5" data-testid="system-card-cap">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#FFC20E]">
                <Gauge className="w-4 h-4" />
                <div className="font-display font-semibold text-sm">Engine Max Cycles / Tick</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
                default {data?.defaults?.heartbeat_max_cycles_per_tick ?? '—'} &middot; 5&ndash;500
              </span>
            </div>
            <input
              type="number"
              min={5}
              max={500}
              step={1}
              className="sc-input mt-3"
              value={form.heartbeat_max_cycles_per_tick}
              onChange={(e) => setForm((f) => ({ ...f, heartbeat_max_cycles_per_tick: e.target.value }))}
              data-testid="system-max-cap"
            />
            <p className="text-[11px] text-[var(--sc-text-dim)] mt-2">Hard ceiling on cycles processed per tick. Range 5&ndash;500.</p>
          </div>

          <div className="sc-glass rounded-2xl p-5 md:col-span-2" data-testid="system-card-alert-ceiling">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#FFC20E]">
                <BellRing className="w-4 h-4" />
                <div className="font-display font-semibold text-sm">Admin Off-Chain Alert Ceiling (USD)</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
                default {data?.defaults?.admin_alert_ceiling != null ? fmtUsd(data.defaults.admin_alert_ceiling) : '—'}
              </span>
            </div>
            <input
              className="sc-input mt-3"
              value={form.admin_alert_ceiling}
              onChange={(e) => setForm((f) => ({ ...f, admin_alert_ceiling: e.target.value }))}
              data-testid="system-alert-ceiling"
            />
            <p className="text-[11px] text-[var(--sc-text-dim)] mt-2">
              When accumulated off-chain admin revenue reaches or crosses this threshold, every admin screen will render a Steelers-Gold advisory banner
              prompting a unified withdrawal request from the NOWPayments merchant account.
            </p>
          </div>

          <div className={`sc-glass rounded-2xl p-5 md:col-span-2 ${form.under_attack_mode_enabled ? 'sc-uam-card--armed' : ''}`} data-testid="system-card-uam">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-[#FFC20E]">
                <ShieldAlert className="w-4 h-4" />
                <div className="font-display font-semibold text-sm">Under-Attack Mode (UAM)</div>
              </div>
              <label className="sc-uam-toggle" data-testid="system-uam-toggle">
                <input
                  type="checkbox"
                  checked={Boolean(form.under_attack_mode_enabled)}
                  onChange={(e) => setForm((f) => ({ ...f, under_attack_mode_enabled: e.target.checked }))}
                />
                <span className="sc-uam-toggle__track">
                  <span className="sc-uam-toggle__thumb" />
                </span>
                <span className="sc-uam-toggle__label">{form.under_attack_mode_enabled ? 'ARMED' : 'STAND-DOWN'}</span>
              </label>
            </div>

            <p className="text-[11px] text-[var(--sc-text-dim)] mt-2 max-w-2xl">
              When armed, every visitor must complete a one-time PoW challenge + checkbox before any /api request is served. Issued pass-cookies are HMAC-bound
              to the source IP and remain valid for the TTL below. Complements Cloudflare's edge protection — does not replace it.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1">PoW Difficulty (3-6)</div>
                <input
                  type="number"
                  min={3}
                  max={6}
                  className="sc-input"
                  value={form.under_attack_difficulty}
                  onChange={(e) => setForm((f) => ({ ...f, under_attack_difficulty: Math.max(3, Math.min(6, Number(e.target.value) || 5)) }))}
                  data-testid="system-uam-difficulty"
                />
                <div className="text-[10px] text-[var(--sc-text-mute)] mt-1">3 = ~200 ms · 5 = ~2 s · 6 = ~30 s on average</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1">Pass-cookie TTL (seconds)</div>
                <input
                  type="number"
                  min={60}
                  max={86400}
                  className="sc-input"
                  value={form.under_attack_cookie_ttl_seconds}
                  onChange={(e) => setForm((f) => ({ ...f, under_attack_cookie_ttl_seconds: Number(e.target.value) || 1800 }))}
                  data-testid="system-uam-ttl"
                />
                <div className="text-[10px] text-[var(--sc-text-mute)] mt-1">Default 1800 (30 min) · Range 60 – 86400</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1">Custom Visitor Message</div>
                <input
                  type="text"
                  maxLength={280}
                  placeholder="Leave blank to use the default copy."
                  className="sc-input"
                  value={form.under_attack_message}
                  onChange={(e) => setForm((f) => ({ ...f, under_attack_message: e.target.value }))}
                  data-testid="system-uam-message"
                />
                <div className="text-[10px] text-[var(--sc-text-mute)] mt-1">{280 - (form.under_attack_message || '').length} chars left</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Position Pool Reconciliation — always visible, even when the
          data fetch above failed. This is the emergency-recovery surface
          after a Redis wipe. */}
      <div className="sc-glass rounded-2xl p-5 md:p-6" data-testid="system-card-reconcile">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-[#FFC20E]">
            <Recycle className="w-4 h-4" />
            <div className="font-display font-semibold text-sm">Position Pool Reconcile</div>
          </div>
          <button
            type="button"
            onClick={reconcile}
            disabled={reconciling}
            className="sc-btn-gold"
            style={{ padding: '10px 16px', fontSize: 12 }}
            data-testid="system-reconcile-btn"
          >
            {reconciling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Reconcile Now
          </button>
        </div>
        <p className="text-[11px] text-[var(--sc-text-dim)] mt-3 leading-relaxed">
          Recovers positions that drifted out of the Redis pool relative to the sub-pack ledger (e.g., after a Redis wipe). First position re-enqueued becomes
          the new genesis seed if the matrix has no root.{' '}
          <span className="text-[var(--sc-text-mute)]">Runs automatically on every backend boot &mdash; this button is a manual override.</span>
        </p>
        {lastReconcile ? (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]" data-testid="system-reconcile-report">
            <div className="rounded-lg bg-[rgba(255,182,18,0.06)] border border-[rgba(255,182,18,0.20)] px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)]">Expected</div>
              <div className="font-display font-bold text-[15px] sc-tabular">{lastReconcile.expected_total_releases ?? '—'}</div>
            </div>
            <div className="rounded-lg bg-[rgba(255,182,18,0.06)] border border-[rgba(255,182,18,0.20)] px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)]">In Pool</div>
              <div className="font-display font-bold text-[15px] sc-tabular">{lastReconcile.currently_in_pool ?? '—'}</div>
            </div>
            <div className="rounded-lg bg-[rgba(255,182,18,0.06)] border border-[rgba(255,182,18,0.20)] px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)]">Cycled</div>
              <div className="font-display font-bold text-[15px] sc-tabular">{lastReconcile.completed_positions ?? '—'}</div>
            </div>
            <div
              className={`rounded-lg px-3 py-2 border ${
                (lastReconcile.reenqueued ?? 0) > 0
                  ? 'bg-[rgba(255,194,14,0.12)] border-[rgba(255,194,14,0.55)]'
                  : 'bg-[rgba(255,255,255,0.04)] border-[rgba(255,255,255,0.14)]'
              }`}
            >
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)]">Re-enqueued</div>
              <div className="font-display font-bold text-[15px] sc-tabular text-[#FFC20E]">
                {lastReconcile.reenqueued ?? 0}
                {lastReconcile.first_was_genesis_seed ? <span className="text-[10px] ml-1">+ root</span> : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Plant Root Seed — bootstrap the matrix or seed after a wipe */}
      <div className="sc-glass rounded-2xl p-5 md:p-6" data-testid="system-card-seed-root">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-[#FFC20E]">
            <Sprout className="w-4 h-4" />
            <div className="font-display font-semibold text-sm">Plant Root Seed</div>
          </div>
          <button
            type="button"
            onClick={seedRoot}
            disabled={seeding}
            className="sc-btn-gold"
            style={{ padding: '10px 16px', fontSize: 12 }}
            data-testid="system-seed-root-btn"
          >
            {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sprout className="w-3.5 h-3.5" />}
            Plant Root
          </button>
        </div>
        <p className="text-[11px] text-[var(--sc-text-dim)] mt-3 leading-relaxed">
          Drops one fresh root position into the matrix queue, owned by you. The matrix engine pairs it with fillers on the next tick. You earn the cycle reward
          when it completes; the root re-enters the queue automatically. Press as many times as needed.
        </p>
        {lastSeed ? (
          <div className="mt-3 text-[11px] text-[var(--sc-text-dim)] flex items-center gap-2" data-testid="system-seed-root-result">
            <span className="sc-pill" style={{ background: 'rgba(255,194,14,0.10)', borderColor: 'rgba(255,194,14,0.45)' }}>
              <span className="sc-dot" />
              Last planted
            </span>
            <span className="font-mono text-[10px] text-[var(--sc-text-mute)]">{lastSeed.position_id?.slice(0, 10)}…</span>
          </div>
        ) : null}
      </div>

      {/* FACTORY RESET — DANGER ZONE.
          Always-expanded so the admin can never lose visibility of it.
          Confirmation guard is the typed-phrase + the WIPE EVERYTHING
          button \u2014 that's the only protection layer that actually matters. */}
      <div
        className="rounded-2xl border-2 p-5 md:p-6"
        style={{
          background: 'rgba(220, 38, 38, 0.04)',
          borderColor: 'rgba(248, 113, 113, 0.45)',
        }}
        data-testid="system-card-factory-reset"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[#FF8A82]">
            <Flame className="w-4 h-4" />
            <div className="font-display font-semibold text-sm">Danger Zone &mdash; Factory Reset</div>
          </div>
          <span
            className="text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full"
            style={{
              background: 'rgba(248,113,113,0.18)',
              border: '1px solid rgba(248,113,113,0.55)',
              color: '#FF8A82',
            }}
          >
            Irreversible
          </span>
        </div>
        <div className="mt-4">
          <p className="text-[11px] text-[var(--sc-text-dim)] leading-relaxed">
            Wipes <strong className="text-[#FF8A82]">EVERYTHING</strong>: all users, sub packs, positions (queue + archive), ledger entries, withdrawals, batch
            payouts, deposits, support tickets, referral tracking, admin pool. Redis pool queues are emptied. Your own admin account is preserved exactly (login
            still works), but its balances are zeroed. The runtime System Variable Governor settings (heartbeat interval, UAM, etc.) are kept.
          </p>
          <p className="text-[11px] text-[#FF8A82] mt-2 font-semibold">This cannot be undone.</p>
          <label htmlFor={`${uid}-wipe-phrase`} className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mt-4 mb-1.5">
            Type <span className="text-[#FF8A82]">WIPE EVERYTHING</span> to confirm
          </label>
          <input
            id={`${uid}-wipe-phrase`}
            type="text"
            className="sc-input"
            value={wipePhrase}
            onChange={(e) => setWipePhrase(e.target.value)}
            placeholder="WIPE EVERYTHING"
            data-testid="system-wipe-phrase"
          />
          <button
            type="button"
            onClick={factoryReset}
            disabled={wiping || wipePhrase !== 'WIPE EVERYTHING'}
            className="w-full mt-4 px-4 py-3 rounded-xl font-display font-bold text-sm transition flex items-center justify-center gap-2"
            style={{
              background: wipePhrase === 'WIPE EVERYTHING' && !wiping ? 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)' : 'rgba(255,255,255,0.04)',
              color: wipePhrase === 'WIPE EVERYTHING' && !wiping ? '#FFFFFF' : 'var(--sc-text-mute)',
              border: wipePhrase === 'WIPE EVERYTHING' && !wiping ? '1px solid rgba(248,113,113,0.65)' : '1px solid rgba(255,255,255,0.10)',
              cursor: wipePhrase === 'WIPE EVERYTHING' && !wiping ? 'pointer' : 'not-allowed',
            }}
            data-testid="system-factory-reset-btn"
          >
            {wiping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flame className="w-4 h-4" />}
            Wipe Everything
          </button>
        </div>
      </div>

      <div className="sc-glass-strong rounded-2xl p-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] flex items-center gap-2">
          <Shield className="w-3.5 h-3.5 text-[#FFC20E]" />
          {data?.updated_at ? `Last update ${String(data.updated_at).slice(0, 19).replace('T', ' ')}` : 'Defaults active — no override yet'}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={restore} className="sc-btn-ghost" style={{ padding: '10px 14px', fontSize: 12 }} data-testid="system-restore">
            <RotateCcw className="w-3.5 h-3.5" />
            Restore defaults
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty()}
            className="sc-btn-gold"
            style={{ padding: '10px 18px', fontSize: 12 }}
            data-testid="system-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save Variables
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemSubpage;
